import { ConfirmationModal } from 'views/components';
import { config } from 'config';
import isEmpty from 'lodash/isEmpty';
import { optionActions } from 'core/Option';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Checkbox,
    Container,
    ExpandButton,
    FormGroup,
    IconButton,
    Input,
    Modal,
    Table
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    clearObject,
    parseBool,
    parseLanguageLabel
} from 'shared/utils';
import {
    useDispatch,
    useSelector
} from 'hooks';
const {
    admission,
    availableLanguages,
    consult,
    interconsult,
    turns
} = config;

const Specialties = () => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        configurations: {
            integrations
        }
    } = useSelector('config');
    const showExternalCode = integrations.includes('OSPEPRI');
    const {
        specialties: list = [],
        specialtiesLoading: loading,
        specialtiesRegistrationError,
        specialtiesSuccess
    } = useSelector('option');

    const [deleteConfirm, setDeleteConfirm] = useState(0);
    const [errors, setErrors] = useState({});
    const [modalEdit, setModalEdit] = useState(false);
    const [selectedSpecialty, setSelectedSpecialty] = useState({});

    const getUnparsedSpecialties = useCallback((filter = {}) => dispatch(optionActions.getSpecialties({
        ...filter,
        unparsed: true
    })), []);

    useEffect(() => {
        specialtiesSuccess && onCancel();
    }, [specialtiesSuccess]);
    
    useEffect(() => {
        getUnparsedSpecialties();
    }, [getUnparsedSpecialties]);

    const boolToInt = value => !!value ? 1 : 0;

    const clearErrors = id => setErrors(errors => {
        delete errors[id];
        return errors;
    });

    const onAccept = () => {
        if (validate()) {
            const {
                admission_enabled,
                always_available,
                call_all,
                featured,
                hide_interconsult,
                hide_patients,
                id,
                receive_appointments,
                ...data
            } = selectedSpecialty;
            const newSpecialty = {
                ...data,
                admission_enabled: boolToInt(admission_enabled),
                always_available: boolToInt(always_available),
                call_all: boolToInt(call_all),
                featured: boolToInt(featured),
                hide_interconsult: boolToInt(hide_interconsult),
                hide_patients: boolToInt(hide_patients),
                receive_appointments: boolToInt(receive_appointments)
            };
            if (!!id) {
                dispatch(optionActions.updateSpecialty(clearObject({
                    ...newSpecialty,
                    id
                })));
            } else {
                dispatch(optionActions.createSpecialty(clearObject(newSpecialty)));
            }
        }
    };

    const onCancel = () => {
        dispatch(optionActions.setOptionSpecialtiesRegistrationError({}));
        dispatch(optionActions.setOptionSpecialtiesSuccess(false));
        setErrors({});
        setSelectedSpecialty({});
        setModalEdit(false);
    };

    const onDelete = action => {
        if (action === 'confirm') {
            dispatch(optionActions.deleteSpecialty(deleteConfirm));
        }
        setDeleteConfirm(0);
    };

    const onEditPrompt = (specialty = {}) => {
        setErrors({});
        setSelectedSpecialty(specialty);
        setModalEdit(true);
    };

    const specialtyChange = useCallback((id, value) => {
        if (availableLanguages.includes(id) && !isEmpty(specialtiesRegistrationError)) {
            const newRegistrationError = { ...specialtiesRegistrationError };
            delete newRegistrationError[id];
            dispatch(optionActions.setOptionSpecialtiesRegistrationError(newRegistrationError));
        }
        clearErrors(id);
        setSelectedSpecialty(selectedSpecialty => ({
            ...selectedSpecialty,
            [id]: value
        }));
    }, [specialtiesRegistrationError]);

    const validate = () => {
        const newErrors = {};
        availableLanguages.forEach(language => {
            if (selectedSpecialty[language]) return;
            newErrors[language] = t('Requerido');
        });
        setErrors(newErrors);
        return isEmpty(newErrors);
    };

    const rowActions = useMemo(() => [{
        icon: 'edit',
        onClick: onEditPrompt,
        title: t('Editar')
    }, {
        icon: 'delete',
        onClick: ({ id }) => setDeleteConfirm(id),
        title: t('Eliminar')
    }], [onEditPrompt, setDeleteConfirm]);

    const tableConfig = useMemo(() => [{
        flex: 2,
        key: 'name',
        label: t('Nombre')
    }, {
        display: consult,
        key: 'always_available',
        label: t('Siempre disponible')
    }, {
        display: consult,
        key: 'featured',
        label: t('Destacada')
    }, {
        display: consult,
        key: 'call_all',
        label: t('Llamada general')
    }, {
        display: consult,
        key: 'hide_patients',
        label: t('Oculta espontánea')
    }, {
        display: interconsult,
        key: 'hide_interconsult',
        label: t('Oculta interconsulta')
    }, {
        display: turns,
        key: 'receive_appointments',
        label: t('Recibe turnos')
    }, {
        display: turns,
        key: 'free_pass',
        label: t('Turnos sin validación')
    }, {
        display: admission,
        key: 'admission_enabled',
        label: t('Realiza admisiones')
    }].filter(({ display = true }) => Boolean(display)).map(c => ({
        ...c,
        ...(c.key !== 'name' && { accessor: parseBool })
    })), []);

    return (
        <>
            <ConfirmationModal
                actionHandler={ onDelete }
                active={ !!deleteConfirm }
                title={ t('¿Deseas eliminar esta especialidad?') }
            />
            <Modal active={ modalEdit } borderRadius="25px" width="500px" onToggle={ onCancel }>
                <Container>
                    <Row>
                        {
                            availableLanguages.map((language, index) =>
                                <Col key={ index } xs="12">
                                    <FormGroup label={ `${ t('Nombre') }${ parseLanguageLabel(language) }` }>
                                        <Input
                                            error={ errors[language] ?? specialtiesRegistrationError[language] }
                                            value={ selectedSpecialty[language] ?? '' }
                                            onChange={ value => specialtyChange(language, value) }
                                        />
                                    </FormGroup>
                                </Col>
                            )
                        }
                        {
                            showExternalCode &&
                            <Col xs="12">
                                <FormGroup label={ t('Código externo') }>
                                    <Input
                                        value={ selectedSpecialty?.external_code ?? '' }
                                        onChange={ value => specialtyChange('external_code', value) }
                                    />
                                </FormGroup>
                            </Col>
                        }
                        <Col xs="12">
                            <FormGroup label={ t('Configuración') }>
                                {
                                    tableConfig.map(({ display, key, label }) => {
                                        if (['name'].includes(key) || !display) return null;
                                        return (
                                            <Checkbox
                                                checked={ !!selectedSpecialty[key] }
                                                key={ key }
                                                label={ label }
                                                margin="5px 0px 0px 0px"
                                                onClick={ checked => specialtyChange(key, checked) }
                                            />
                                        );
                                    })
                                }
                            </FormGroup>
                        </Col>
                    </Row>
                    <ButtonFooter margin="20px 0px 0px 0px">
                        <ExpandButton danger caption={ t('Cancelar') } size={ 100 } onClick={ onCancel } />
                        <ExpandButton caption={ t('Confirmar') } loading={ loading } size={ 100 } onClick={ onAccept } />
                    </ButtonFooter>
                </Container>
            </Modal>
            <Container>
                <IconButton
                    icon="add"
                    label={ t('Nueva especialidad') }
                    margin="5px"
                    onClick={ onEditPrompt }
                />
                <Table
                    config={ tableConfig }
                    data={ list }
                    loading={ loading }
                    maxHeight="auto"
                    noDataText={ t('No hay especialidades para mostrar') }
                    rowActions={ rowActions }
                />
            </Container>
        </>
    );
};

export default Specialties;
